'use es6';

import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import getLang from 'I18n/utils/getLang';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Button } from 'foundations-components/transitional/components/Button';
import { Tag } from 'foundations-components/transitional/components/Tag';
import MobileFilterBar from 'marketplace-ui-common/components/mobile/MobileFilterBar';
import MobileTagButton from 'marketplace-ui-common/components/mobile/MobileTagButton';
import { MediaQueryView } from 'marketplace-ui-common/layout/components/MediaQueryView';
import * as ScreenSizes from 'marketplace-ui-common/layout/constants/ScreenSizes';
import { isSucceeded } from 'marketplace-ui-common/requestStates/RequestStateUtils';
import { getCredentialDisplayName } from 'marketplace-ui-common/utils/credentials';
import { Fragment, useCallback, useContext, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as FilterKey from '../../constants/search/FilterKey';
import StorefrontUsageTrackerContext from '../../context/StorefrontUsageTrackerContext';
import { getAccreditations, getAccreditationsRequestState } from '../../selectors/accreditations';
import { getCertifications, getCertificationsRequestState } from '../../selectors/certifications';
import { getIntegrations, getIntegrationsRequestState } from '../../slices/integrationsSlice';
import { getServices } from '../../slices/serviceCatalogSlice';
import { DEFAULT_SORT, updateStorefrontFilters } from '../../slices/storefrontFiltersSlice';
import { getCredentialById } from '../../utils/credentials';
import { getTranslatedSortText, translateFilterValue } from '../../utils/i18n';
import { fromStateToFilterTags } from '../../utils/storefrontFilters';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const textPath = 'ecosystem-marketplace-solutions-ui.storefront';
const sortTextPath = 'ecosystem-marketplace-solutions-ui.storefront.results.sort';
const ResultsFilters = ({
  filters,
  marginBlockEnd,
  marginBlockStart,
  onClearFilters,
  onCloseFilterTag,
  paddingInlineEnd,
  startSlot
}) => {
  const dispatch = useDispatch();
  const {
    trackInteraction
  } = useContext(StorefrontUsageTrackerContext);
  const lang = getLang();
  const certificationsRequestState = useSelector(getCertificationsRequestState);
  const certifications = useSelector(getCertifications);
  const accreditations = useSelector(getAccreditations);
  const accreditationRequestState = useSelector(getAccreditationsRequestState);
  const catalogServices = useSelector(getServices);
  const integrations = useSelector(getIntegrations);
  const integrationsRequestState = useSelector(getIntegrationsRequestState);
  const accreditationsRequestSucceeded = useMemo(() => isSucceeded(accreditationRequestState), [accreditationRequestState]);
  const integrationsRequestSucceeded = useMemo(() => isSucceeded(integrationsRequestState), [integrationsRequestState]);
  const filterTagList = useMemo(() => {
    return fromStateToFilterTags(filters);
  }, [filters]);
  const getTranslatedCredential = useCallback((filterValue, credentialList) => {
    return getCredentialDisplayName(getCredentialById(credentialList, filterValue), lang);
  }, [lang]);
  const getIntegrationName = useCallback(filterValue => {
    return integrations.find(integration => integration.offeringId === parseInt(filterValue, 10)).displayName;
  }, [integrations]);
  const getFilterTranslation = useCallback((filterKey, filterValue) => {
    if (filterKey === FilterKey.CERTIFICATION) {
      return isSucceeded(certificationsRequestState) ? getTranslatedCredential(filterValue, certifications) : '';
    } else if (filterKey === FilterKey.ACCREDITATION) {
      return accreditationsRequestSucceeded ? getTranslatedCredential(filterValue, accreditations) : '';
    } else if (filterKey === FilterKey.INTEGRATION) {
      return integrationsRequestSucceeded ? getIntegrationName(filterValue) : '';
    } else if (filterKey === FilterKey.SERVICE) {
      const serviceFilter = catalogServices.filter(service => service.id === filterValue)[0];
      return filterValue && serviceFilter ? serviceFilter.name : '';
    } else {
      return translateFilterValue(filterKey, filterValue);
    }
  }, [certificationsRequestState, certifications, getIntegrationName, getTranslatedCredential, integrationsRequestSucceeded, accreditationsRequestSucceeded, accreditations, catalogServices]);
  const renderedFilterTags = useMemo(() => {
    return filterTagList.map(({
      key,
      value
    }) => {
      const translatedValue = getFilterTranslation(key, value);
      const tagText = /*#__PURE__*/_jsx(FormattedMessage, {
        message: `${textPath}.filters.${key}.tag`,
        options: {
          value: translatedValue,
          count: translatedValue
        }
      });
      return /*#__PURE__*/_jsx(Tag, {
        closeable: true,
        inline: true,
        onCloseClick: () => {
          onCloseFilterTag([key], [value]);
          trackInteraction('close filter tag', {
            filter: key,
            value
          });
        },
        marginInlineStart: "0",
        marginInlineEnd: "200",
        marginBlock: "100",
        children: tagText
      }, `${key}-${value}`);
    });
  }, [filterTagList, getFilterTranslation, onCloseFilterTag, trackInteraction]);
  const renderedMobileFilterTags = useMemo(() => {
    return filterTagList.map(({
      key,
      value
    }) => {
      const translatedValue = getFilterTranslation(key, value);
      const tagText = /*#__PURE__*/_jsx(FormattedMessage, {
        message: `${textPath}.filters.${key}.tag`,
        options: {
          value: translatedValue,
          count: translatedValue
        }
      });
      return /*#__PURE__*/_jsx(MobileTagButton, {
        onClick: () => {
          onCloseFilterTag([key], [value]);
          trackInteraction('close filter tag', {
            filter: key,
            value
          });
        },
        closeable: true,
        children: tagText
      }, `${key}-${value}`);
    });
  }, [filterTagList, getFilterTranslation, onCloseFilterTag, trackInteraction]);
  const clearSort = useCallback(() => {
    dispatch(updateStorefrontFilters({
      updates: {
        [FilterKey.SORTS]: DEFAULT_SORT
      }
    }));
    trackInteraction('change sort', {
      sortField: DEFAULT_SORT
    });
  }, [dispatch, trackInteraction]);
  const sortTag = useMemo(() => {
    const sortKey = filters[FilterKey.SORTS];
    const tagText = `${I18n.text(`${sortTextPath}.filterTagPreface`)} ${getTranslatedSortText(sortKey)}`;
    return sortKey !== DEFAULT_SORT && /*#__PURE__*/_jsx(MobileTagButton, {
      onClick: clearSort,
      closeable: true,
      children: tagText
    });
  }, [clearSort, filters]);
  const defaultFilterBar = /*#__PURE__*/_jsxs(Flex, {
    alignItems: "center",
    flexWrap: "wrap",
    marginBlockStart: marginBlockStart,
    marginBlockEnd: marginBlockEnd,
    paddingInlineEnd: paddingInlineEnd,
    children: [renderedFilterTags, filterTagList.length > 0 && /*#__PURE__*/_jsx(Button, {
      onClick: () => {
        onClearFilters();
        trackInteraction('clear all filters');
      },
      use: "link",
      marginInlineStart: "300",
      children: /*#__PURE__*/_jsx(Text, {
        size: "small",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `${textPath}.results.clearFilters`
        })
      })
    })]
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(MediaQueryView, {
      min: ScreenSizes.SM,
      children: defaultFilterBar
    }), /*#__PURE__*/_jsx(MediaQueryView, {
      max: ScreenSizes.SM,
      children: /*#__PURE__*/_jsx(View, {
        marginBlockStart: marginBlockStart,
        marginBlockEnd: marginBlockEnd,
        paddingInlineEnd: paddingInlineEnd,
        children: /*#__PURE__*/_jsx(MobileFilterBar, {
          startSlot: startSlot,
          middleSlot: /*#__PURE__*/_jsxs(Fragment, {
            children: [renderedMobileFilterTags, sortTag]
          })
        })
      })
    })]
  });
};
export default ResultsFilters;